import * as React from 'react';
import * as cx from 'classnames';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core';

import { withIntl, IntlProps } from 'ha/i18n';

import { Typography } from 'ha/ui/next/Typography';
import Grid from 'ha/ui/next/Grid';
import Paper from 'ha/ui/next/Paper';
import ContentfulImg from 'ha/modules/Contentful/containers/ContentfulImg';
import units from 'ha/theme/units';
import Link from 'ha/ui/next/Link';
import LazyComponent from 'ha/components/LazyComponent';
import Container from 'ha/ui/next/Container';

import { Blog } from '../types';

interface Props {
  blogs: Blog[];
}

export const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: units.yx(2, 0),
      [theme.breakpoints.up('md')]: {
        padding: units.yx(3, 0),
      },
    },
    card: {
      maxWidth: units.rem(18.75),
    },
    paper: {
      paddingBottom: units.rem(1),
    },
    title: {
      fontSize: units.rem(1.5),
      lineHeight: 1.29,
      marginBottom: units.rem(0.5),
      [theme.breakpoints.up('md')]: {
        lineHeight: 1.23,
        fontSize: units.rem(1.85),
        marginBottom: units.rem(2.8),
      },
    },
    imageWrapper: {
      position: 'relative',
      height: units.rem(15.25),
      marginBottom: units.rem(1),
    },
    image: {
      position: 'absolute',
      objectFit: 'cover',
      height: '100%',
      width: '100%',
    },
    limitedText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
    },
    blogTitle: {
      padding: units.yx(0, 1),
      marginBottom: units.rem(0.5),
    },
    blogDescription: {
      padding: units.yx(0, 1),
    },
  });

export const BlogCards: React.SFC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({ blogs, classes, intl: { T } }) => {
  return (
    <div className={classes.container}>
      <Container>
        <Grid container justify="center">
          <Grid item xs={12}>
            <Typography.H3
              className={classes.title}
              component="h3"
              align="center"
            >
              {T('Blog for landlords')}
            </Typography.H3>
          </Grid>
        </Grid>
        <Grid container justify="center" spacing={24}>
          {blogs.map(blog => {
            return (
              <Grid item key={blog.pathname} className={classes.card}>
                <Link to={blog.pathname}>
                  <Paper className={classes.paper}>
                    <Grid
                      container
                      alignItems="flex-end"
                      justify="center"
                      className={classes.imageWrapper}
                      data-test-locator="BlogCards Image"
                    >
                      <LazyComponent bottomOffset="-50%">
                        <ContentfulImg
                          src={blog.headerImage}
                          className={classes.image}
                          role="presentation"
                        />
                      </LazyComponent>
                    </Grid>

                    <Typography.H3
                      className={cx(classes.limitedText, classes.blogTitle)}
                      component="h4"
                      data-test-locator="BlogCards Title"
                    >
                      {blog.title}
                    </Typography.H3>
                    <Typography.Paragraph
                      className={cx(
                        classes.limitedText,
                        classes.blogDescription,
                      )}
                      component="p"
                      data-test-locator="BlogCards Description"
                    >
                      {blog.description}
                    </Typography.Paragraph>
                  </Paper>
                </Link>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </div>
  );
};

export default withStyles(styles)(withIntl(BlogCards));
